import { useMutation } from "@apollo/client/react";
import {
  CreateMultipleChoiceQuestionOptions,
  CreateMultipleChoiceQuestionOptionsVariables,
  MultipleChoiceType,
} from "__generated__/types";
import { TextButton } from "common/buttons";
import { FieldError } from "common/components/forms/FieldError";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { SubmitButton } from "common/components/forms/SubmitButton";
import { InputField } from "common/components/InputField";
import { H3 } from "common/components/Typography";
import gql from "graphql-tag";
import * as React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

const CREATE_QUESTION_OPTION = gql`
  mutation CreateMultipleChoiceQuestionOptions(
    $input: CreateMultipleChoiceQuestionOptionsInput!
  ) {
    createMultipleChoiceQuestionOptions(input: $input) {
      options {
        title
        id
        correct
      }
    }
  }
`;

interface Props {
  questionId: string;
  multipleChoiceId: string;
  type: MultipleChoiceType;
  refetch?(): void;
}

interface CreateQuestionOption {
  title: string;
  correct: boolean;
}

export const CreateQuestionOptionForm: React.FC<
  React.PropsWithChildren<Props>
> = ({ questionId, multipleChoiceId, type, refetch }) => {
  const [showAddOption, setShowAddOption] = useState(false);
  const methods = useForm<CreateQuestionOption>();
  const { register, handleSubmit } = methods;

  const [createQuestionOption, { loading, error }] = useMutation<
    CreateMultipleChoiceQuestionOptions,
    CreateMultipleChoiceQuestionOptionsVariables
  >(CREATE_QUESTION_OPTION);

  const onSubmit = async (data: CreateQuestionOption) => {
    const result = await createQuestionOption({
      variables: {
        input: {
          multipleChoiceId,
          questionId,
          options: [{ correct: data.correct ?? true, title: data.title }],
        },
      },
    });

    if (result) {
      setShowAddOption(false);
      if (refetch) {
        refetch();
      }
    }
  };

  return (
    <>
      {showAddOption && (
        <>
          <H3>Legg til et nytt svaralternativ</H3>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <input
                type="checkbox"
                id="new-option-correct"
                defaultChecked={type == MultipleChoiceType.GROUPING}
                disabled={type == MultipleChoiceType.GROUPING}
                {...register("correct")}
              />
              <InputField
                id="new-option-title"
                kind="normal"
                placeholder="Skriv inn alternativ.."
                aria-label="Alternativ"
                register={register("title", {
                  required: "Svaralternativ kan ikke være tomt",
                  minLength: 1,
                })}
              />
              <FieldError name="title" />
              <SubmitButton kind="primary" loading={loading}>
                Lagre
              </SubmitButton>
              <MutationLoadingOrError loading={loading} error={error} />
            </form>
          </FormProvider>
        </>
      )}
      {!showAddOption && (
        <TextButton
          textSize="small"
          onClick={() => {
            setShowAddOption(true);
          }}
          kind="secondary"
        >
          + Legg til alternativ
        </TextButton>
      )}
    </>
  );
};
